import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiPlusLight,
  PiSparkleLight,
  PiCheckLight,
} from 'react-icons/pi';

export type MatterIntent = 'quick' | 'analyze';

interface MatterIntentSelectorProps {
  value: MatterIntent | null;
  onChange: (intent: MatterIntent) => void;
}

const MatterIntentSelector: React.FC<MatterIntentSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const options: {
    key: MatterIntent;
    icon: React.ReactNode;
    title: string;
    description: string;
    bullets: string[];
  }[] = [
    {
      key: 'quick',
      icon: <PiPlusLight className="w-6 h-6" />,
      title: t('Quick Create'),
      description: t('Set up a matter quickly. Add documents and run analysis later.'),
      bullets: [
        t('Only name required'),
        t('Instant creation'),
        t('Add details anytime'),
      ],
    },
    {
      key: 'analyze',
      icon: <PiSparkleLight className="w-6 h-6" />,
      title: t('Create & Analyze'),
      description: t('Upload documents and run PONS-powered legal research now.'),
      bullets: [
        t('Documents required'),
        t('PONS research & generation'),
        t('Guided step-by-step setup'),
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {options.map((opt) => {
        const isSelected = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            data-tour-id={opt.key === 'quick' ? 'intent-quick-create' : 'intent-create-analyze'}
            onClick={() => onChange(opt.key)}
            className={`relative flex flex-col items-start gap-3 rounded-xl border-2 p-5 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
              isSelected
                ? 'border-primary-500 bg-primary-50/60 dark:border-primary-600 dark:bg-primary-900/30 shadow-sm'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
            }`}
          >
            {isSelected && (
              <div className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-primary-500 text-white">
                <PiCheckLight className="w-3 h-3" />
              </div>
            )}
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                isSelected
                  ? 'bg-primary-100 text-primary-600 dark:bg-primary-800 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {opt.icon}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {opt.title}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {opt.description}
              </p>
            </div>
            <ul className="mt-1 space-y-1">
              {opt.bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <span
                    className={`flex-shrink-0 w-1 h-1 rounded-full ${
                      isSelected ? 'bg-primary-500' : 'bg-gray-400 dark:bg-gray-500'
                    }`}
                  />
                  {b}
                </li>
              ))}
            </ul>
          </button>
        );
      })}
    </div>
  );
};

export default MatterIntentSelector;
